import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import jwt from 'jsonwebtoken';

import EmailSignIn from './components/EmailSignIn.js';
import DefaultSignIn from './components/DefaultSignIn.js';
import VerifyAccount from './components/VerifyAccount.js';

import FirebaseAuthManager from '../utils/firebaseAuthManager.js';
import FirebaseEventManager from '../utils/firebaseEventManager.js';
import AmplitudeEventManager from '../utils/amplitudeEventManager.js';

import './styles/signin.css';
let timeoutIndex = null;
const isFirstSignInCacheKey = 'ALFRED:SIGNED';
function SignIn(props) {
  const firebaseAuthManager = new FirebaseAuthManager();
  const firebaseEventManager = new FirebaseEventManager();

  const [isEmailLogin, setIsEmailLogin] = useState(!props.backToSignIn);
  const [emailCache, setEmailCache] = useState('');
  const [passwordCache, setPasswordCache] = useState('');
  const [user, setUser] = useState(firebaseAuthManager.getCurrentUser());

  let loginType = 'manual';
  let provider = '';
  let startTime = new Date().getTime();
  let registerDate = ''
  useEffect(() => {
    firebaseEventManager.signInPageLanded(props.productVersion);

    let isFirstSignInCache = localStorage.getItem(isFirstSignInCacheKey);
    if (!isFirstSignInCache) {
      loginType = 'first';
    }
    async function main() {
      let isLogin = false;
      try {
        isLogin = await firebaseAuthManager.isLogin();

        if (isLogin) {
          const user = firebaseAuthManager.getCurrentUser();
          const idToken = await user.getIdToken();
          const decodeInfo = jwt.decode(idToken);
          const providerList = {
            'password': 'email',
            'google.com': 'google',
            'apple.com': 'apple'
          }
          loginType = 'quick';
          provider = decodeInfo && providerList[decodeInfo.firebase.sign_in_provider] || decodeInfo.firebase.sign_in_provider;
          registerDate = new Date(user.metadata.creationTime).toISOString();
          AmplitudeEventManager.setSignInChannelAndRegisterDate(provider, registerDate);
          startTime = new Date().getTime();
          await onSignInSuccess();
        } else {
          await props.onNotSignIn();
        }
      } catch (error) {
        console.error(error);
      }
    }
    main();

    return () => {};
  }, []);

  useEffect(() => {
    props.onEmailSignIn(isEmailLogin);
  }, [isEmailLogin]);

  useEffect(() => {
    setIsEmailLogin(!props.backToSignIn);
  }, [props.backToSignIn]);

  async function signInWithGoogle() {
    if (isEmailLogin) {
      firebaseEventManager.signInEmailPageClick({
        item: 'google',
      });
    } else {
      firebaseEventManager.signInPageClickItem({
        item: 'google_login',
      });
    }
    startTime = new Date().getTime();
    provider = 'google';
    try {
      const { user } = await firebaseAuthManager.signInWithGoogle();
      registerDate = new Date(user.metadata.creationTime).toISOString();
      AmplitudeEventManager.setSignInChannelAndRegisterDate(provider, registerDate);
      await onSignInSuccess();
    } catch (error) {
      firebaseEventManager.sendLoginFailed({
        provider,
        type: loginType,
        code: error && error.code,
      });
      AmplitudeEventManager.signInSuccess({ success: false, method: provider });
    }
  }

  async function signInWithApple() {
    if (isEmailLogin) {
      firebaseEventManager.signInEmailPageClick({
        item: 'apple',
      });
    } else {
      firebaseEventManager.signInPageClickItem({
        item: 'apple_login',
      });
    }
    startTime = new Date().getTime();
    provider = 'apple';
    try {
      const { user } = await firebaseAuthManager.signInWithApple();
      registerDate = new Date(user.metadata.creationTime).toISOString();
      AmplitudeEventManager.setSignInChannelAndRegisterDate(provider, registerDate);
      await onSignInSuccess();
    } catch (error) {
      firebaseEventManager.sendLoginFailed({
        provider,
        type: loginType,
        code: error && error.code,
      });
      AmplitudeEventManager.signInSuccess({ success: false, method: provider });
    }
  }

  async function signInWithEmail({ email, password, error = null }) {
    setEmailCache(email);
    setPasswordCache(password);
    let errorString = null;
    let errorCode = null;
    startTime = new Date().getTime();
    provider = 'email';
    try {
      if (error !== null) {
        throw error;
      }
      let user;

      await Promise.race([
        timeout(),
        (async function () {
          user = await firebaseAuthManager.signInWithEmail({
            email,
            password,
          });

          setIsEmailLogin(false);
          await onSignInSuccess();
        })(),
      ]);
      registerDate = new Date(user.metadata.creationTime).toISOString();
      AmplitudeEventManager.setSignInChannelAndRegisterDate(provider, registerDate);

      return true;
    } catch (error) {
      errorCode = error && error.code;
      AmplitudeEventManager.signInSuccess({ success: false, method: provider });

      if (error === 'timeout') {
        return props.apiError();
      }
      if (error === 'ExceedTryMax') {
        errorString = 'pwd_retry_limit';
        return props.retryPasswordTooManyTimes();
      }
      if (error.code === 'auth/network-request-failed') {
        return props.networkError();
      }
      if (error.code === 'auth/too-many-requests') {
        return props.apiError();
      }
      if (error.code === 'auth/too-many-requests') {
        return props.apiError();
      }
      throw error;
    } finally {
      clearTimeout(timeoutIndex);
      if (errorString !== null) {
        firebaseEventManager.sendLoginFailed({
          provider,
          type: loginType,
          code: errorCode,
          customError: errorString,
        });
      }
    }
  }

  async function onSignInSuccess() {
    localStorage.setItem(isFirstSignInCacheKey, '1');
    const user = firebaseAuthManager.getCurrentUser();
    setUser(user);
    if (user && user.emailVerified === true && user.refreshToken) {
      const idToken = await user.getIdToken();
      const accountMail = user.email;
      let shouldUpdate = false;
      let name = user.displayName;
      if (!name) {
        const profile = user.providerData.find(profile => profile.displayName);
        if (profile) {
          name = profile.displayName;
          shouldUpdate = !!name;
        }
      }
      let image = user.photoURL;
      if (provider === 'email' || image === null) {
        image = 'https://cdn.my-alfred.com/emailUserAvatar.png';
        shouldUpdate = true;
      }

      if (shouldUpdate) {
        await user.updateProfile({
          displayName: name,
          photoURL: image,
        });
      }

      props.onSignInSuccess({
        idToken,
        accountMail,
        name,
        image,
      });

      firebaseEventManager.sendInitialLoginSucceeded({
        provider,
        email: user.email,
        type: loginType,
      });

      firebaseEventManager.logingTime({
        provider,
        btnclickLogin: new Date().getTime() - startTime,
      });
    } else {
      if (user.emailVerified === false) {
        firebaseEventManager.sendLoginFailed({
          provider: 'email',
          type: loginType,
          code: '',
          customError: 'unverified',
        });
      }

      await props.onNotSignIn();
      setIsEmailLogin(true);
    }
  }

  async function goSignInWithEmailPage() {
    if (user && user.email) {
      await firebaseAuthManager.signOut();
    }
    firebaseEventManager.signInPageClickItem({
      item: 'email_login',
    });
    setIsEmailLogin(true);
  }

  async function verifyAccountAgain() {
    firebaseEventManager.verifyPageClick();
    const result = await signInWithEmail({
      email: emailCache,
      password: passwordCache,
    });
    if (result !== true) {
      return;
    }

    const user = firebaseAuthManager.getCurrentUser();
    if (user.emailVerified === false) {
      props.unVerifiedError();
    }
  }

  function downloadApp({ platform, page }) {
    let iOSLink = 'https://alfredcamera.app/store/webviewer_emailsignup_ios';
    let androidLink = 'https://alfredcamera.app/store/webviewer_emailsignup_android';
    if (props.role === 'camera') {
      iOSLink = 'https://alfredcamera.app/store/webcamera_emailsignup_ios'
      androidLink = 'https://alfredcamera.app/store/webcamera_emailsignup_android'
    }
    window.open(platform === 'ios' ? iOSLink : androidLink );
  }

  // show nothing
  if (props.isLoginFinish === false) {
    return <div></div>;
  }

  if (user && user.emailVerified === false) {
    if (emailCache === '') {
      firebaseAuthManager.signOut();
      return setUser(null);
    }

    return <VerifyAccount downloadApp={downloadApp} email={user.email} verifyAccountAgain={verifyAccountAgain} />;
  }

  if (isEmailLogin) {
    return (
      <EmailSignIn
        downloadApp={downloadApp}
        signInWithGoogle={signInWithGoogle}
        signInWithEmail={signInWithEmail}
        signInWithApple={signInWithApple}
        fetchSignInMethodsForEmail={(email) => firebaseAuthManager.fetchSignInMethodsForEmail(email)}
      />
    );
  }

  if (props.children) {
    return React.Children.map(props.children, child => {
      // checking isValidElement is the safe way and avoids a typescript error too
      const props = { signInWithGoogle, signInWithApple, goSignInWithEmailPage };
      // When child is DOM element then typeof child.type === 'string' 
      // For preventing react throws warning, then filter DOM element
      if (React.isValidElement(child) && typeof child.type !== 'string') {
        return React.cloneElement(child, props);
      }
      return child;
    });
  } else {
    return (
      <DefaultSignIn
        backgroundImage={props.backgroundImage}
        signInWithGoogle={signInWithGoogle}
        signInWithApple={signInWithApple}
        goSignInWithEmailPage={goSignInWithEmailPage}
      />
    );
  }
}

async function timeout() {
  await new Promise((res, rej) => {
    timeoutIndex = setTimeout(() => {
      rej('timeout');
    }, 60000);
  });
}

SignIn.propTypes = {
  isLoginFinish: PropTypes.bool,
  backToSignIn: PropTypes.bool,
  backgroundImage: PropTypes.string,
  role: PropTypes.string,
  productVersion: PropTypes.string,
  onSignInSuccess: PropTypes.func,
  retryPasswordTooManyTimes: PropTypes.func,
  networkError: PropTypes.func,
  apiError: PropTypes.func,
  unVerifiedError: PropTypes.func,
  onNotSignIn: PropTypes.func,
  onEmailSignIn: PropTypes.func,
};

export default SignIn;
